import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staffAPI } from '../services/api';
import toast from '../utils/toast';

// CV Templates
export const useCVTemplates = (params = {}) => {
  return useQuery({
    queryKey: ['cv', 'templates', params],
    queryFn: async () => {
      const response = await staffAPI.getCVTemplates(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useCVTemplate = (id) => {
  return useQuery({
    queryKey: ['cv', 'templates', id],
    queryFn: async () => {
      const response = await staffAPI.getCVTemplate(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });
};

// Project Involvement
export const useProjectInvolvements = (params = {}) => {
  return useQuery({
    queryKey: ['cv', 'project-involvement', params],
    queryFn: async () => {
      const response = await staffAPI.getProjectInvolvements(params);
      return {
        results: response.data?.results || response.data || [],
        count: response.data?.count || 0,
        next: response.data?.next,
        previous: response.data?.previous,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useProjectInvolvement = (id) => {
  return useQuery({
    queryKey: ['cv', 'project-involvement', id],
    queryFn: async () => {
      const response = await staffAPI.getProjectInvolvement(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateProjectInvolvement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => staffAPI.createProjectInvolvement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cv', 'project-involvement'] });
      toast.success('✅ Project involvement created successfully');
    },
    onError: (error) => {
      // Extract error message from DRF ValidationError response
      let errorMessage = 'Failed to create project involvement';
      
      if (error.response?.data) {
        const data = error.response.data;
        
        // Handle non-field errors (string or array)
        if (data.error) {
          errorMessage = Array.isArray(data.error) ? data.error[0] : data.error;
        } else if (data.detail) {
          errorMessage = Array.isArray(data.detail) ? data.detail[0] : data.detail;
        } else if (data.non_field_errors) {
          errorMessage = Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors;
        } else if (typeof data === 'string') {
          errorMessage = data;
        } else if (Array.isArray(data)) {
          errorMessage = data[0];
        } else {
          // Handle field-specific errors (extract first field error)
          const fieldErrors = Object.values(data).flat();
          if (fieldErrors.length > 0) {
            errorMessage = Array.isArray(fieldErrors[0]) ? fieldErrors[0][0] : fieldErrors[0];
          }
        }
      }
      
      toast.error(`❌ ${errorMessage}`);
    },
  });
};

export const useUpdateProjectInvolvement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.updateProjectInvolvement(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cv', 'project-involvement'] });
      queryClient.invalidateQueries({ queryKey: ['cv', 'project-involvement', variables.id] });
      toast.success('✅ Project involvement updated successfully');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Failed to update project involvement';
      toast.error(`❌ ${errorMessage}`);
    },
  });
};

export const useDeleteProjectInvolvement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => staffAPI.deleteProjectInvolvement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cv', 'project-involvement'] });
      toast.success('✅ Project involvement deleted successfully');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Failed to delete project involvement';
      toast.error(`❌ ${errorMessage}`);
    },
  });
};

export const useSyncProjectInvolvement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (staffId) => staffAPI.syncProjectInvolvement(staffId),
    onSuccess: (_, staffId) => {
      queryClient.invalidateQueries({ queryKey: ['cv', 'project-involvement'] });
      queryClient.invalidateQueries({ queryKey: ['cv', 'project-involvement', 'sync-summary', staffId] });
      toast.success('✅ Project involvement synced successfully');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Failed to sync project involvement';
      toast.error(`❌ ${errorMessage}`);
    },
  });
};

export const useSyncAllProjectInvolvements = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => staffAPI.syncAllProjectInvolvements(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cv', 'project-involvement'] });
      toast.success('✅ All project involvements synced successfully');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Failed to sync project involvements';
      toast.error(`❌ ${errorMessage}`);
    },
  });
};

export const useProjectInvolvementSyncSummary = (staffId) => {
  return useQuery({
    queryKey: ['cv', 'project-involvement', 'sync-summary', staffId],
    queryFn: async () => {
      const response = await staffAPI.getProjectInvolvementSyncSummary(staffId);
      return response.data;
    },
    enabled: !!staffId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useEmployeesWithoutProjectData = () => {
  return useQuery({
    queryKey: ['cv', 'project-involvement', 'employees-without-data'],
    queryFn: async () => {
      const response = await staffAPI.getEmployeesWithoutProjectData();
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

// CV Generation
export const useGenerateCV = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => staffAPI.generateCV(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cv', 'generation-logs'] });
      toast.success('✅ CV generation initiated');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Failed to generate CV';
      toast.error(`❌ ${errorMessage}`);
    },
  });
};

export const useGenerateBatchCVs = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => staffAPI.generateBatchCVs(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['cv', 'generation-logs'] });
      const message = response.data?.message || 'Batch CV generation initiated';
      toast.success(`✅ ${message}`);
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Failed to generate batch CVs';
      toast.error(`❌ ${errorMessage}`);
    },
  });
};

export const usePreviewCVData = (staffId) => {
  return useQuery({
    queryKey: ['cv', 'preview', 'data', staffId],
    queryFn: async () => {
      const response = await staffAPI.previewCVData(staffId);
      return response.data;
    },
    enabled: !!staffId,
    staleTime: 2 * 60 * 1000,
  });
};

export const usePreviewCVHTML = (staffId, templateId) => {
  return useQuery({
    queryKey: ['cv', 'preview', 'html', staffId, templateId],
    queryFn: async () => {
      const response = await staffAPI.previewCVHTML(staffId, templateId);
      return response.data;
    },
    enabled: !!staffId && !!templateId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useCVMergeFields = () => {
  return useQuery({
    queryKey: ['cv', 'merge-fields'],
    queryFn: async () => {
      const response = await staffAPI.getCVMergeFields();
      return response.data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - merge fields don't change often
  });
};

// Active CV Templates (for dropdown)
export const useActiveCVTemplates = () => {
  return useQuery({
    queryKey: ['cv', 'templates', 'active'],
    queryFn: async () => {
      const response = await staffAPI.getActiveCVTemplates();
      return response.data || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Batch Data Quality
export const useBatchDataQuality = (staffIds = [], options = {}) => {
  return useQuery({
    queryKey: ['cv', 'data-quality', staffIds],
    queryFn: async () => {
      const response = await staffAPI.getBatchDataQuality(staffIds);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

// CV Generation History
export const useCVGenerationLogs = (params = {}) => {
  return useQuery({
    queryKey: ['cv', 'generation-logs', params],
    queryFn: async () => {
      const response = await staffAPI.getCVGenerationLogs(params);
      return {
        results: response.data?.results || response.data || [],
        count: response.data?.count || 0,
        next: response.data?.next,
        previous: response.data?.previous,
      };
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useCVGenerationLog = (id) => {
  return useQuery({
    queryKey: ['cv', 'generation-logs', id],
    queryFn: async () => {
      const response = await staffAPI.getCVGenerationLog(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
};

